import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useDarkMode } from "../../context/darkModeContext";
import { CircularProgress } from "@mui/material";
// import Chrome from "@uiw/react-color-chrome";
import { GithubPlacement } from "@uiw/react-color-github";
import { api } from "../../utils/axios-instance";
import { useDepartment } from "../../stores/departmentStore";
import { DEPARTMENT_COLORS } from "../../styles/departmentColors";

const EditDept = ({ id, name, color }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deptName, setDeptName] = useState(name || "");
  const { darkMode } = useDarkMode();
  const [hex, setHex] = useState("#4F46E5");
  const [loading, setLoading] = useState(false); // State for loading
  const listDepartments = useDepartment((state) => state.listDepartments); // Get listDepartments function from store

  useEffect(() => {
    setDeptName(name || "");
    setHex(color || "#8B5CF6");
  }, [name, color]);

  const openDialog = (e) => {
    //  Stop the event from bubbling up the DOM tree
    e.stopPropagation();

    setDeptName(name || "");
    setHex(color || "#8B5CF6");

    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const handleDepartmentNameChange = async () => {
    setLoading(true); // Set loading to true when API call starts
    let body = {
      name: deptName,
      metadata: {
        bg: hex,
        border: "#B7B6C2",
      },
    };

    try {
      let renameDept = await api.patch(`/dept/updateDept/${id}`, body);

      if (renameDept) {
        await listDepartments();
        setLoading(false); // Set loading back to false after successful response
        closeDialog(); // Close dialog after successful response
      }
    } catch (error) {
      console.log("error occurred at rename department.", error);
      setLoading(false); // Set loading back to false if there's an error
    }
  };

  const handleInputChange = useCallback((e) => {
    setDeptName(e.target.value);
  }, []);

  return (
    <div aria-hidden className="w-full h-full">
      <button
        onClick={(e) => {
          e.stopPropagation();
          openDialog(e);
        }}
        className="text-sm w-full h-full text-left"
      >
        Edit
      </button>

      <Dialog
        open={isOpen}
        onClose={closeDialog}
        PaperProps={{
          style: {
            borderRadius: "12px",
            minWidth: "420px",
            maxWidth: "480px",
            backgroundColor: darkMode ? "#1E293B" : "#fff",
            color: darkMode ? "#E2E8F0" : "#111827",
          },
        }}
      >
        <DialogTitle
          className="text-xl font-semibold"
          sx={{ color: darkMode ? "#fff" : "#111827" }}
        >
          Rename Department / Reset Color
        </DialogTitle>
        <DialogContent
          style={{ backgroundColor: darkMode ? "#0F172A" : "#F7F8FA" }}
        >
          <div className="my-2 w-full py-2 space-y-4">
            <span className="w-full block">
              <p
                className={`mb-2 ${darkMode ? "text-slate-300" : "text-gray-700"}`}
              >
                Department Name <span className="text-red-500">*</span>
              </p>

              <input
                className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent ${darkMode ? "bg-[#1E293B] border-slate-600 text-white placeholder-slate-400" : "bg-white border-gray-300 text-gray-900"}`}
                type="text"
                placeholder="e.g. Engineering"
                value={deptName}
                onChange={handleInputChange}
                autoFocus
              />
            </span>

            <div>
              <p
                className={`mb-3 ${darkMode ? "text-slate-300" : "text-gray-700"}`}
              >
                Color
              </p>

              <div className="grid grid-cols-6 sm:grid-cols-7 gap-3">
                {DEPARTMENT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setHex(color)}
                    className="relative w-11 h-11 rounded-xl transition-all duration-200 hover:scale-110 hover:shadow-lg active:scale-95"
                    style={{
                      backgroundColor: color,
                      border:
                        hex === color
                          ? "3px solid #111827"
                          : "2px solid rgba(255,255,255,0.9)",
                      boxShadow:
                        hex === color
                          ? "0 0 0 3px rgba(99,102,241,0.35)"
                          : "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    {hex === color && (
                      <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3 mt-3">
                <span
                  className="w-8 h-8 rounded-full border border-gray-200 flex-shrink-0"
                  style={{ backgroundColor: hex }}
                />

                <p
                  className={`text-sm ${darkMode ? "text-slate-400" : "text-gray-600"}`}
                >
                  Selected: <span className="font-mono font-medium">{hex}</span>
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <button
            className={`px-4 py-2 rounded-lg border transition-colors ${darkMode ? "border-slate-600 text-slate-300 hover:bg-slate-700" : "border-gray-300 hover:bg-gray-50"}`}
            onClick={closeDialog}
          >
            Cancel
          </button>
          <button
            className="flex gap-2 items-center px-4 py-2 rounded-lg bg-[#5E5ADB] text-white hover:bg-[#4C47C7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleDepartmentNameChange}
            disabled={loading || !deptName.trim()}
          >
            Rename Department / Reset Color
            {loading && <CircularProgress size={16} color="inherit" />}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditDept;
